<template>
  <div class="hero">
    <b-row class="hero-container">
      <b-col :lg="image ? '7' : '12'" class="hero-content">
        <slot />

        <form class="mt-30" @submit.prevent="submit">
          <AlertSlideUpDown :show="!!anyErrorMessage">
            <div v-html="errorMessages"></div>
          </AlertSlideUpDown>

          <b-row>
            <b-col v-if="!jobRoles" md="4" sm="12">
              <div class="hidden-sm-down">
                <h4 class="pb-20">Job category</h4>

                <b-form-checkbox-group
                  v-model="form.categories"
                  :options="categoryOptions"
                  stacked
                  @input="$v.form.categories.$touch()"
                ></b-form-checkbox-group>
              </div>
              <div class="visible-sm-down">
                <h6 class="pb-10">Select your categories and subscribe</h6>

                <button
                  v-for="(option, i) in categoryOptions"
                  :key="option.value"
                  class="btn btn-sm mb-10 mr-10"
                  :class="{
                    'btn-outline': form.categories.indexOf(i) === -1
                  }"
                  type="button"
                  @click.prevent="toggleCategory(i)"
                >
                  {{ option.text }}
                </button>
              </div>
            </b-col>
            <b-col v-else>
              <h4 class="pb-20">Role</h4>

              <b-form-group>
                <b-form-checkbox-group
                  v-model="form.roles"
                  :options="roles"
                  stacked
                  @input="$v.form.roles.$touch()"
                >
                </b-form-checkbox-group>
              </b-form-group>
            </b-col>

            <b-col v-if="jobPositionTypes">
              <h4 class="pb-20">Positions</h4>

              <b-form-group>
                <b-form-checkbox-group
                  v-model="form.positionTypes"
                  :options="positionTypes"
                  stacked
                  @input="$v.form.positionTypes.$touch()"
                ></b-form-checkbox-group>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="8">
              <div v-if="fullLocation">
                <h4 class="pb-20">Location</h4>

                <b-form-row>
                  <b-col sm="3">
                    <b-form-select
                      v-model="form.countryCode"
                      class="form-select-item mb-10"
                      style="background-color: #fff"
                      :options="siteCountries"
                      @change="onCountryChange"
                    ></b-form-select>
                  </b-col>

                  <b-col>
                    <FormItem
                      invalid-message="Please enter location"
                      :state="
                        $v.form.location.$dirty
                          ? !$v.form.location.$error
                          : null
                      "
                      required
                      class="mb-10"
                    >
                      <FormInputAddressAutoComplete
                        v-if="!disableAddressSearch"
                        v-model="form.location"
                        :state="
                          $v.form.location.$dirty
                            ? !$v.form.location.$error
                            : null
                        "
                        placeholder="City or area"
                        :component-restrictions="componentRestrictions"
                        type="(regions)"
                        class="component-search-bar__item--address"
                        input-class="form-control"
                      >
                      </FormInputAddressAutoComplete>
                    </FormItem>
                  </b-col>
                </b-form-row>
              </div>

              <div style="display: flex">
                <b-form-select
                  v-if="!fullLocation"
                  v-model="form.countryCode"
                  class="form-select-item"
                  style="
                    background-color: #fff;
                    flex: 0 0 auto;
                    width: 120px;
                    margin-right: 0.5rem;
                  "
                  :options="siteCountriesSmall"
                  @change="onCountryChange"
                ></b-form-select>

                <div style="flex: 1 1 auto">
                  <FormItem
                    invalid-message="Please enter your email address"
                    :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
                    required
                    style="flex: 1 1 auto"
                    class="mb-10"
                  >
                    <b-form-input
                      v-model="form.email"
                      :state="
                        $v.form.email.$dirty ? !$v.form.email.$error : null
                      "
                      placeholder="Email"
                    />
                  </FormItem>
                </div>
              </div>

              <b-row class="justify-content-end">
                <b-col md="auto">
                  <button type="submit" class="btn w-100">
                    <span v-if="!isRequesting">Subscribe</span>
                    <b-spinner v-else small />
                  </button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </form>
      </b-col>

      <b-col v-if="image" lg="5" class="hero-pattern hidden-md-down">
        <img
          src="~/assets/components/subscribe/pattern.png"
          alt="pattern"
          class="pattern"
        />
        <img
          :src="require('~/assets/components/subscribe/phone2.png')"
          alt="phone"
          class="phone"
        />
      </b-col>
    </b-row>

    <b-modal
      v-model="showSuccess"
      centered
      modal-class="modal-bb-squared newsletter-modal"
      hide-footer
    >
      <template slot="default">
        <div>
          <h4 class="mb-20">You’ve got mail!</h4>
          <p>
            Check your inbox and confirm your subscription to start receiving
            emails about new jobs
          </p>
        </div>

        <button class="btn mt-30" @click.prevent="showSuccess = false">
          Close
        </button>
      </template>
    </b-modal>

    <b-modal
      v-model="showUpdateSuccess"
      centered
      modal-class="modal-bb-squared newsletter-modal"
      hide-footer
    >
      <template slot="default">
        <div>
          <h4 class="mb-20">Your preferences was updated</h4>
          <p>
            We updated your existing subscription of digest email. Visit “Email
            notifications” under your settings for the full overview of your job
            criterias.
          </p>
        </div>

        <button class="btn mt-30" @click.prevent="showUpdateSuccess = false">
          Close
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { required, requiredIf, email } from 'vuelidate/lib/validators'
import FormItem from '@/components/form/FormItem'
import AlertSlideUpDown from '@/components/AlertSlideUpDown'
import FormInputAddressAutoComplete from '@/components/form/FormInputAddressAutoComplete'
import { convertCountryCodeForMaps } from '@/services/GoogleMaps'
import ApplicantProfileModel from '@/models/ApplicantProfile'

export default {
  components: {
    FormItem,
    AlertSlideUpDown,
    FormInputAddressAutoComplete
  },
  props: {
    jobPositionTypes: Boolean,
    jobRoles: Boolean,
    image: Boolean,
    fullLocation: Boolean
  },
  data() {
    const countryCode = this.$store.getters['meta/sitePreferredCountryCode']

    return {
      isRequesting: false,
      promptErrorMessage: undefined,
      showSuccess: false,
      showUpdateSuccess: false,
      form: {
        countryCode,
        email: '',
        categories: [],
        roles: [],
        positionTypes: [],
        location: undefined
      },
      applicantProfile: undefined
    }
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters['user/isLoggedIn']
    },
    userEmail() {
      return this.$store.state.user.data.email
    },
    userCurrentProfile() {
      return this.$store.getters['user/currentProfile']
    },
    disableAddressSearch() {
      if (this.fullLocation && this.form.countryCode === 'REMOTE') {
        return true
      }
      return false
    },
    categories() {
      return this.$store.getters['meta/jobCategories']
    },
    categoryOptions() {
      return this.categories.map((item, i) => {
        return {
          text: item.name,
          value: i
        }
      })
    },
    positionTypes() {
      return this.$store.getters['meta/jobPositionTypes'].map((item) => ({
        text: item.label,
        value: item.id
      }))
    },
    roles() {
      return this.$store.getters['meta/jobRoles'].map((item) => ({
        text: item.label,
        value: item.value
      }))
    },
    siteCountries() {
      return this.$store.getters['meta/siteCountriesInclRemoteExNI']
    },
    siteCountriesSmall() {
      return this.siteCountries.map((i) => ({
        value: i.value,
        text: i.value
      }))
    },
    componentRestrictions() {
      return this.form.countryCode
        ? {
            country: convertCountryCodeForMaps(this.form.countryCode)
          }
        : undefined
    },
    anyErrorMessage() {
      return (
        !!this.promptErrorMessage ||
        this.$v.form.categories.$error ||
        this.$v.form.roles.$error ||
        this.$v.form.positionTypes.$error
      )
    },
    errorMessages() {
      const messages = []

      if (this.promptErrorMessage) {
        messages.push(this.promptErrorMessage)
      }

      if (this.$v.form.categories.$error) {
        messages.push('Please, select at least one category.')
      }

      if (this.$v.form.roles.$error) {
        messages.push('Please, select at least one role.')
      }

      if (this.$v.form.positionTypes.$error) {
        messages.push('Please, select at least one position.')
      }

      return messages.join('<br>')
    }
  },
  async mounted() {
    try {
      if (
        this.isLoggedIn &&
        this.userEmail &&
        this.userCurrentProfile.type === 'applicant'
      ) {
        const { data } = await this.$axios.get('digest/mail/' + this.userEmail)
        this.applicantProfile = new ApplicantProfileModel(data)
      }
    } catch (err) {
      console.log('err', err)
    }
  },
  methods: {
    onResetLocationValue() {
      this.form.location = undefined
      this.$nextTick(() => {
        this.$v.$reset()
      })
    },
    toggleCategory(index) {
      const found = this.form.categories.indexOf(index)

      if (found !== -1) {
        this.form.categories.splice(found, 1)
      } else {
        this.form.categories.push(index)
      }

      this.$v.form.categories.$touch()
    },
    onCountryChange() {
      this.onResetLocationValue()
    },
    async submit() {
      this.$v.form.$touch()

      if (this.$v.form.$error) {
        return
      }

      if (this.isRequesting) {
        return
      }

      this.isRequesting = true

      try {
        this.promptErrorMessage = undefined

        const roles = []

        if (this.jobRoles) {
          roles.push(...this.form.roles)
        } else {
          this.categories
            .filter((item, i) => {
              return this.form.categories.includes(i)
            })
            .forEach((item) => {
              roles.push(...item.roles)
            })
        }

        const body = {
          email: this.form.email,
          jobRoles: roles,
          jobPositionTypes: this.form.positionTypes
        }

        const location = {
          countryCode: this.form.countryCode
        }

        if (this.fullLocation) {
          location.address = this.form.location
        }

        body.locations = [location]

        if (
          this.applicantProfile &&
          this.applicantProfile.email === body.email
        ) {
          body.settingsKey = this.applicantProfile.settingsKey
          await this.$store.dispatch('jobs/updateSubscription', body)
          this.showUpdateSuccess = true
        } else {
          await this.$store.dispatch('jobs/subscribe', body)

          this.showSuccess = true

          this.$gtm.push({ event: 'fb', fbEvent: 'DigestSubmit' })
          this.$gtm.push({
            event: 'ga.event',
            category: 'subscribe',
            action: 'DigestSubmit'
          })
        }
      } catch (err) {
        this.promptErrorMessage = err.message
      }

      this.isRequesting = false
    }
  },
  validations() {
    return {
      form: {
        email: {
          required,
          email
        },
        location: this.fullLocation
          ? {
              required: requiredIf(function() {
                return !this.form.countryCode === 'REMOTE'
              })
            }
          : {},
        roles: this.jobRoles
          ? {
              required
            }
          : {},
        positionTypes: this.jobPositionTypes
          ? {
              required
            }
          : {},
        categories: !this.jobRoles
          ? {
              required
            }
          : {}
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hero {
  padding-top: rem(20px);
  padding-bottom: rem(40px);

  .hero-container {
    position: relative;
    margin: 0 auto;
    background-color: $blue-200;
    padding: rem(50px) !important;
    min-height: 560px;
    // overflow: hidden;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;

    @include media-breakpoint-up(xl) {
      max-width: 1360px !important;
    }

    @include media-breakpoint-down(md) {
      padding: rem(25px) rem(15px) !important;
      min-height: initial;
    }

    @include media-breakpoint-down(sm) {
      height: auto;
    }

    @include media-breakpoint-down(xs) {
      .col-12 {
        padding: 0;
      }
    }
  }

  .hero-content {
    position: relative;
    z-index: 2;

    h1 {
      font-size: 40px;

      @include media-breakpoint-down(md) {
        font-size: 32px;
      }

      @include media-breakpoint-down(sm) {
        font-size: 24px;
      }
    }

    p {
      font-size: 20px;

      @include media-breakpoint-down(md) {
        font-size: 16px;
      }
    }
  }

  .hero-pattern {
    flex: 0 0 auto;
    position: static;

    .pattern {
      position: absolute;
      right: rem(50px);
      height: 500px;
      width: auto;
      top: 50%;
      margin-top: -250px;
    }

    .phone {
      position: absolute;
      bottom: 0;
      right: rem(50px);
      height: auto;
      width: auto;
      max-width: 366px;
    }

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }
}
</style>
